import '../../misc/neb-icon';

import { LitElement, html, css } from 'lit';

import { CORE_API_URL } from '../../../../packages/neb-utils/env';
import { formatPhoneNumber } from '../../../../packages/neb-utils/formatters';
import {
  baseStyles,
  CSS_SPACING,
  CSS_FONT_FAMILY,
  CSS_FONT_SIZE_BODY,
  CSS_COLOR_HIGHLIGHT,
  CSS_FONT_WEIGHT_BOLD,
} from '../../../styles';

const LOCATIONSELECTORS = {
  bookingNames: { selector: '[id^=booking-name-]' },
  bookingAddress1s: { selector: '[id^=booking-address-]' },
  bookingAddress2s: { selector: '[id^=booking-address2-]' },
  bookingCityStateZipcodes: { selector: '[id^=booking-city-state-zipcode-]' },
  bookingPhoneNumbers: { selector: '[id^=booking-phone-number-]' },
  bookingVcfLinks: { selector: '[id^=booking-vcf-link-]' },

  walkinNames: { selector: '[id^=walkin-name-]' },
  walkinAddress1s: { selector: '[id^=walkin-address-]' },
  walkinAddress2s: { selector: '[id^=walkin-address2-]' },
  walkinCityStateZipcodes: { selector: '[id^=walkin-city-state-zipcode-]' },
  walkinPhoneNumbers: { selector: '[id^=walkin-phone-number-]' },
  walkinVcfLinks: { selector: '[id^=walkin-vcf-link-]' },

  otherNames: { selector: '[id^=other-name-]' },
  otherAddress1s: { selector: '[id^=other-address-]' },
  otherAddress2s: { selector: '[id^=other-address2-]' },
  otherCityStateZipcodes: { selector: '[id^=other-city-state-zipcode-]' },
  otherPhoneNumbers: { selector: '[id^=other-phone-number-]' },
  otherVcfLinks: { selector: '[id^=other-vcf-link-]' },
};

export const ELEMENTS = {
  name: { id: 'name' },
  address1: { id: 'address' },
  address2: { id: 'address2' },
  bio: { id: 'bio' },
  cityStateZipcode: { id: 'city-state-zipcode' },
  logo: { id: 'logo' },
  onlineBookingAvailableHeader: { id: 'online-booking-available-header' },
  walkInOnlyHeader: { id: 'walk-in-only-header' },
  otherLocationsHeader: { id: 'other-locations-header' },
  phoneNumber: { id: 'phone-number' },
  vcfLink: { id: 'vcf-link' },
  website: { id: 'website' },
  ...LOCATIONSELECTORS,
};

const LOCATION = {
  ONLINEBOOK: {
    header: 'onlineBookingAvailableHeader',
    label: 'Online Booking Available',
    value: 'booking',
  },
  WALKIN: {
    header: 'walkInOnlyHeader',
    label: 'Walk-In Only',
    value: 'walkin',
  },
  OTHER: {
    header: 'otherLocationsHeader',
    label: 'Other Locations',
    value: 'other',
  },
};

function createModelLocations() {
  return {
    [LOCATION.ONLINEBOOK.value]: [],
    [LOCATION.WALKIN.value]: [],
    [LOCATION.OTHER.value]: [],
  };
}

class NebPagePracticeInfo extends LitElement {
  static get properties() {
    return {
      logoSrc: String,
      model: Object,
      vcardHref: String,
      apiPrefix: String,
      tenantShortName: String,
      enableBooking: Boolean,
      enableWalkIn: Boolean,
      layout: {
        type: String,
        reflect: true,
      },
      locationBuckets: Object,
    };
  }

  static get styles() {
    return [
      baseStyles,
      css`
        :host {
          display: flex;
          flex-direction: column;
        }

        :host([layout='small']) .container-content {
          display: flex;
          flex-direction: column;
        }

        .logo {
          max-width: 171px;
          max-height: 114px;
          background: white;
          text-align: center;
          margin-bottom: 30px;
        }

        .container {
          display: flex;
          flex-direction: column;
          margin-top: ${CSS_SPACING};
          text-decoration: underline;
          font-size: ${CSS_FONT_SIZE_BODY};
        }

        .container-content {
          display: flex;
          flex-direction: column;
          margin-top: ${CSS_SPACING};
          font-size: ${CSS_FONT_SIZE_BODY};
        }

        .header {
          font-size: 16px;
          font-weight: ${CSS_FONT_WEIGHT_BOLD};
          display: grid;
        }

        .location-name {
          display: flex;
          flex-direction: column;
          font-weight: ${CSS_FONT_WEIGHT_BOLD};
        }

        .address {
          display: flex;
          flex-direction: column;
          text-decoration: underline;
          cursor: pointer;
        }

        .phone {
          display: flex;
          flex-direction: column;
          text-decoration: underline !important;
          cursor: pointer;
        }

        .bio-without-website,
        .website {
          padding-bottom: ${CSS_SPACING};
        }

        .download-vcf {
          font-family: ${CSS_FONT_FAMILY};
          font-size: ${CSS_FONT_SIZE_BODY};
          color: ${CSS_COLOR_HIGHLIGHT};
          vertical-align: bottom;
        }

        .download-anchor-class {
          display: flex;
          padding-bottom: ${CSS_SPACING};
        }

        .download-vcard-icon {
          margin-top: 2px;
          fill: ${CSS_COLOR_HIGHLIGHT};
          width: 23px;
          height: 23px;
        }

        .download-vcard-text {
          padding-top: 5px;
          padding-left: 5px;
          text-decoration: underline;
        }
      `,
    ];
  }

  constructor() {
    super();

    this.__initState();
    this.__initHandlers();
  }

  __initState() {
    this.logoSrc = '';
    this.vcardHref = '';
    this.apiPrefix = '';
    this.tenantShortName = '';
    this.layout = '';
    this.locationBuckets = createModelLocations();
    this.enableBooking = false;
    this.enableWalkIn = false;
    this.model = {
      name: '',
      address: {},
      locations: [],
      email: '',
      bio: '',
      phoneNumber: '',
      website: '',
    };
  }

  __initHandlers() {
    this.__handlers = {
      openMap: e => {
        const arr = e.target.getAttribute('id').split('-');
        const bucket = arr[0];
        const index = arr.pop();

        if (this.locationBuckets[bucket]) {
          this.__openMapUrl(index, bucket);
        } else {
          this.__openMapUrl(0);
        }
      },
    };
  }

  // eslint-disable-next-line complexity
  __computeSpecificLocationVCardHref(locationId) {
    if (!this.apiPrefix || !this.tenantShortName || !locationId) {
      return '';
    }

    return `${CORE_API_URL}/public/v1/${
      this.tenantShortName
    }/downloadVCard?locationId=${locationId}`;
  }

  __renderBio() {
    const styling = !this.model.website ? 'bio-without-website' : '';

    return this.model.bio
      ? html`
          <div id="${ELEMENTS.bio.id}" class="${styling}">
            ${this.model.bio}
          </div>
        `
      : '';
  }

  __renderLogo() {
    return this.logoSrc
      ? html`
          <img
            id="${ELEMENTS.logo.id}"
            class="logo"
            sizing="contain"
            src="${this.logoSrc}"
            position="left"
            onerror="this.error=null; this.style.display='none'"
          />
        `
      : '';
  }

  __renderPracticeAddress() {
    return this.model.address
      ? html`
          <div class="address">
            <a id="${ELEMENTS.address1.id}" @click="${this.__handlers.openMap}"
              >${this.model.address.address1}</a
            >

            <a id="${ELEMENTS.address2.id}" @click="${this.__handlers.openMap}"
              >${this.model.address.address2
                ? this.model.address.address2
                : ''}</a
            >

            <a
              id="${ELEMENTS.cityStateZipcode.id}"
              @click="${this.__handlers.openMap}"
              >${this.model.address.city}, ${this.model.address.state}
              ${this.model.address.zipCode}</a
            >
          </div>
        `
      : '';
  }

  __renderPhoneNumber() {
    return this.model.locations?.[0]?.phoneNumber
      ? html`
          <span
            ><a
              id="${ELEMENTS.phoneNumber.id}"
              class="link phone"
              href="tel:+1${this.model.locations[0].phoneNumber}"
              >${formatPhoneNumber(this.model.locations[0].phoneNumber)}</a
            ></span
          >
        `
      : '';
  }

  __renderWebsite() {
    return this.model.website
      ? html`
          <span class="website"
            ><a
              id="${ELEMENTS.website.id}"
              class="link"
              target="_blank"
              href="http://${this.model.website}"
              >${this.model.website}</a
            ></span
          >
        `
      : '';
  }

  __renderDownloadVCF() {
    return this.vcardHref
      ? html`
          <div id="downloadVCF" class="download-vcf">
            <a
              id="${ELEMENTS.vcfLink.id}"
              class="link download-anchor-class"
              target="_blank"
              href="${this.vcardHref}"
            >
              <neb-icon
                class="download-vcard-icon"
                icon="neb:download"
              ></neb-icon>

              <div class="download-vcard-text">Save as Contact</div>
            </a>
          </div>
        `
      : '';
  }

  __buildFullAddress(address, address2, city, state, zipCode) {
    return [
      address,
      ...(address2 ? [address2] : []),
      city,
      state,
      zipCode,
    ].join(', ');
  }

  __openMapUrl(index = 0, bucket = 'unspecified') {
    const locationList = this.locationBuckets[bucket]
      ? this.locationBuckets[bucket]
      : this.model.locations;

    const fullAddress = this.__buildFullAddress(
      locationList[index].address1,
      locationList[index].address2,
      locationList[index].city,
      locationList[index].state,
      locationList[index].zipCode,
    );

    const isApple =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    const toolUrl =
      (isApple
        ? 'http://maps.apple.com/?q='
        : 'https://www.google.com/maps/place/') + fullAddress;

    window.open(toolUrl, '_blank');
  }

  __renderLocationHeader({ header, label }) {
    return html`
      <div id="${ELEMENTS[header].id}" class="header">${label}</div>
    `;
  }

  __renderLocationList(modelName) {
    const model = LOCATION[modelName];
    const locations = this.locationBuckets[model.value];
    return locations.length
      ? html`
          ${this.__renderLocationHeader(model)}
          <div class="container-content">
            ${locations
              .sort((x, y) => {
                const firstLocationName = x.name;
                const secondLocationName = y.name;

                return firstLocationName.localeCompare(secondLocationName);
              })
              .map(
                (location, index) => html`
                  <div
                    class="location-name"
                    id="${model.value}-${ELEMENTS.name.id}-${index}"
                  >
                    ${location.name}
                  </div>

                  <a
                    class="address"
                    id="${model.value}-${ELEMENTS.address1.id}-${index}"
                    @click="${this.__handlers.openMap}"
                    >${location.address1}</a
                  >

                  <a
                    class="address"
                    id="${model.value}-${ELEMENTS.address2.id}-${index}"
                    @click="${this.__handlers.openMap}"
                    >${location.address2 ? location.address2 : ''}</a
                  >

                  <a
                    class="address"
                    id="${model.value}-${ELEMENTS.cityStateZipcode.id}-${index}"
                    @click="${this.__handlers.openMap}"
                    >${location.city}, ${location.state} ${location.zipCode}</a
                  >

                  <a
                    id="${model.value}-${ELEMENTS.phoneNumber.id}-${index}"
                    class="link phone"
                    href="tel:+1${location.phoneNumber}"
                    >${formatPhoneNumber(location.phoneNumber)}</a
                  >

                  <a
                    id="${model.value}-${ELEMENTS.vcfLink.id}-${index}"
                    class="link download-anchor-class"
                    target="_blank"
                    href="${this.__computeSpecificLocationVCardHref(
                      location.id,
                    )}"
                  >
                    <neb-icon
                      class="download-vcard-icon"
                      icon="neb:download"
                    ></neb-icon>

                    <div class="download-vcard-text">Save as Contact</div>
                  </a>
                `,
              )}
          </div>
        `
      : '';
  }

  render() {
    return this.model
      ? html`
          ${this.__renderLogo()} ${this.__renderBio()} ${this.__renderWebsite()}
          ${this.model.locations
            ? html`
                ${this.model.locations.length > 1
                  ? html`
                      ${this.__renderLocationList('ONLINEBOOK')}
                      ${this.__renderLocationList('WALKIN')}
                      ${this.__renderLocationList('OTHER')}
                    `
                  : html`
                      <div class="container">
                        ${this.__renderPracticeAddress()}
                        ${this.__renderPhoneNumber()}
                      </div>

                      ${this.__renderDownloadVCF()}
                    `}
              `
            : html`
                <div class="container">
                  ${this.__renderPracticeAddress()}
                  ${this.__renderPhoneNumber()}
                </div>

                ${this.__renderDownloadVCF()}
              `}
        `
      : html``;
  }

  updated(changedProps) {
    if (
      (changedProps.has('model') ||
        changedProps.has('enableBooking') ||
        changedProps.has('enableWalkIn')) &&
      this.model &&
      this.model.locations
    ) {
      this.locationBuckets = this.model.locations.reduce((acc, loc) => {
        const { allowOnlineBooking, allowWalkInAppointments, addressOnly } =
          loc;

        let key = LOCATION.OTHER.value;

        if (this.enableWalkIn && allowWalkInAppointments && !addressOnly) {
          key = LOCATION.WALKIN.value;
        }

        if (this.enableBooking && allowOnlineBooking && !addressOnly) {
          key = LOCATION.ONLINEBOOK.value;
        }

        return { ...acc, [key]: [...acc[key], loc] };
      }, createModelLocations());
    }
  }
}
customElements.define('neb-page-practice-info', NebPagePracticeInfo);
